import {
  DEFAULT_LANG,
  EMBEDDED_PARAM,
  LANG_PARAM,
  LANG_STORAGE_KEY,
  SUPPORTED_LANGS,
} from '../constant';

/**
 * 瀏覽器語言偵測：把瀏覽器回報的語言代碼對應到網站支援的語系。
 */

// 中文依書寫系統區分繁簡：以這些地區／文字代碼開頭的視為繁體中文
const TRADITIONAL_CHINESE_TAGS = ['zh-tw', 'zh-hk', 'zh-mo', 'zh-hant'];
const TRADITIONAL_CHINESE = 'zh-tw';
const SIMPLIFIED_CHINESE = 'zh-cn';

const matchLanguage = function (
  candidate: string,
  supported: readonly string[],
): string | undefined {
  const normalized = candidate.trim().toLowerCase().replace(/_/g, '-');
  if (!normalized) {
    return undefined;
  }

  const exact = supported.find(lang => lang.toLowerCase() === normalized);
  if (exact) {
    return exact;
  }

  const base = normalized.split('-')[0];
  if (base === 'zh') {
    const isTraditional = TRADITIONAL_CHINESE_TAGS.some(tag =>
      normalized.startsWith(tag),
    );
    const target = isTraditional ? TRADITIONAL_CHINESE : SIMPLIFIED_CHINESE;
    return supported.find(lang => lang.toLowerCase() === target);
  }

  return supported.find(lang => lang.toLowerCase().split('-')[0] === base);
};

/**
 * 取得瀏覽器偏好語言清單（依偏好順序）。
 */
export const getBrowserLanguages = function (): string[] {
  if (typeof navigator === 'undefined') {
    return [];
  }
  if (navigator.languages && navigator.languages.length > 0) {
    return Array.from(navigator.languages);
  }
  return navigator.language ? [navigator.language] : [];
};

/**
 * 依序比對候選語言，回傳第一個能對應到支援語系的值；皆無法對應時回 undefined。
 * 單一候選的比對順序：完全相同（不分大小寫）> 中文繁簡對應 > 同主語言（如 ja → ja-JP）。
 */
export const resolveSupportedLanguage = function (
  candidates: readonly string[],
  supported: readonly string[],
): string | undefined {
  for (const candidate of candidates) {
    const matched = matchLanguage(candidate, supported);
    if (matched) {
      return matched;
    }
  }
  return undefined;
};

/**
 * 登入／註冊／忘記密碼頁的顯示語系：網址帶 ?lang= 時以它為準
 * （嵌入頁讀不到平台那邊使用者選的語系，只能由平台帶進來）——對應得到支援語系就用，
 * 對應不到（例如平台有韓文、本站沒有）改用 DEFAULT_LANG，不拿本站記住的語系頂替；
 * 網址沒帶才沿用本站記住的語系。
 */
export const resolveAuthPageLanguage = function (
  search: string,
  stored: string | null,
  supported: readonly string[],
): string | null {
  const requested = new URLSearchParams(search).get(LANG_PARAM);
  if (!requested) {
    return stored;
  }
  return resolveSupportedLanguage([requested], supported) ?? DEFAULT_LANG;
};

/**
 * 進站時決定「記住的語系」要不要改寫，回傳該寫入的值；不需改寫回 null。
 * - 網址帶 ?lang= 且本站支援：記住它，多語搜尋連結（hreflang）點進來整站就是那個語言。
 *   嵌入頁（?embedded=true）與被嵌在 iframe 裡的頁面例外——那是外層當下的語系，
 *   不該蓋掉使用者在本站自己選的（例如後台「聯絡詢問」的表單預覽 iframe 帶 lang=en-US，
 *   與後台同網域、共用記住的語系，曾讓使用者下一次換頁整站變英文）。
 * - 否則還沒記住任何語系的新訪客用 DEFAULT_LANG。
 */
export const resolveInitialStoredLanguage = function (
  search: string,
  stored: string | null,
  isFramed = false,
): string | null {
  const params = new URLSearchParams(search);
  const requested = params.get(LANG_PARAM);
  if (requested && params.get(EMBEDDED_PARAM) !== 'true' && !isFramed) {
    const matched = resolveSupportedLanguage([requested], SUPPORTED_LANGS);
    if (matched) {
      return matched === stored ? null : matched;
    }
  }
  return stored ? null : DEFAULT_LANG;
};

/** 進站時套用 resolveInitialStoredLanguage 的結果。 */
export const initStoredLanguage = function (): void {
  const next = resolveInitialStoredLanguage(
    window.location.search,
    localStorage.getItem(LANG_STORAGE_KEY),
    window.self !== window.top,
  );
  if (next) {
    localStorage.setItem(LANG_STORAGE_KEY, next);
  }
};

/**
 * 進站時的顯示語系（整站只在這裡決定一次，各頁不再各自套用記住的語系）：
 * 網址帶本站支援的 ?lang= 用它（嵌入頁也是，只是不記住），否則用記住的語系，都沒有用 DEFAULT_LANG。
 */
export const resolveBootLanguage = function (
  search: string,
  stored: string | null,
): string {
  const requested = new URLSearchParams(search).get(LANG_PARAM) ?? '';
  return (
    resolveSupportedLanguage([requested], SUPPORTED_LANGS) ??
    stored ??
    DEFAULT_LANG
  );
};
